import React, { useState, useCallback, useMemo } from 'react';
import { Ticket, RefreshCw } from 'lucide-react';
import useAutoCache from '../hooks/useAutoCache';
import CalendarTicketTitle from './CalendarTicketTitle';
import { useI18n } from '../utils/i18n';
import '../styles/PoolMonitor.css';

// #16 (W6): Pool-Monitor — Nachfolger der alten /m/tickets-Ansicht.
// Zeigt ausschließlich die offenen, nicht zugewiesenen Tickets (den Pool)
// als große Karten. Gedacht für den Überwachungsmonitor im Büro (Deep-Link
// /pool) und als vergrößerte Pool-Ansicht aus dem Dashboard; auf dem Handy
// über das Burger-Menü erreichbar. Sortierung wie die Pool-Spalte des
// Dashboards: neue Aktivität zuerst, wartend nach unten, dann Priorität,
// dann älteste Aktivität.
const PRIORITY_ORDER = { kritisch: 1, hoch: 2, normal: 3, niedrig: 4 };

const PRIORITY_COLORS = {
  kritisch: 'var(--color-danger)',
  hoch: 'var(--color-orange)',
  normal: 'var(--color-info)',
  niedrig: 'var(--color-secondary)'
};

const STATUS_COLORS = {
  offen: 'var(--color-info)',
  'in Bearbeitung': 'var(--color-status-pool-in-bearbeitung)',
  wartend: 'var(--color-status-pool-wartend)'
};

const PoolMonitor = () => {
  const { t } = useI18n();
  const [tickets, setTickets] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const handleData = useCallback((ticketData) => {
    setTickets(Array.isArray(ticketData) ? ticketData : []);
    setLastUpdated(new Date());
    setLoaded(true);
  }, []);

  // Eigener Poller (Header pausiert seinen auf /pool); die Glocken-/
  // Benachrichtigungslogik läuft in useAutoCache automatisch mit.
  useAutoCache({ type: 'tickets', enabled: true, onDataUpdate: handleData, checkInterval: 30000 });

  const poolTickets = useMemo(() => {
    return tickets
      .filter(tk => {
        const isUnassigned = !tk.assignedTo || String(tk.assignedTo).trim() === '';
        return isUnassigned && tk.status !== 'abgeschlossen';
      })
      .sort((a, b) => {
        const aNew = a.hasNewActivity || a.hasNewEmails;
        const bNew = b.hasNewActivity || b.hasNewEmails;
        if (aNew && !bNew) return -1;
        if (!aNew && bNew) return 1;
        if (!aNew && !bNew) {
          const aWaiting = a.status === 'wartend';
          const bWaiting = b.status === 'wartend';
          if (!aWaiting && bWaiting) return -1;
          if (aWaiting && !bWaiting) return 1;
        }
        const pa = PRIORITY_ORDER[a.priority] || 5;
        const pb = PRIORITY_ORDER[b.priority] || 5;
        if (pa !== pb) return pa - pb;
        const at = new Date(a.lastActivityAt || a.createdAt || 0).getTime();
        const bt = new Date(b.lastActivityAt || b.createdAt || 0).getTime();
        return at - bt;
      });
  }, [tickets]);

  const formatAge = (dateValue) => {
    if (!dateValue) return null;
    const diffMs = Date.now() - new Date(dateValue).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return t('pool.justNow', 'Gerade eben');
    if (mins < 60) return t('pool.minutesAgo', `vor ${mins} Min.`, { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('pool.hoursAgo', `vor ${hours} Std.`, { count: hours });
    const days = Math.floor(hours / 24);
    return t('pool.daysAgo', `vor ${days} Tg.`, { count: days });
  };

  const openTicket = (ticketId) => {
    try {
      window.dispatchEvent(new CustomEvent('openTicketDetailById', { detail: { ticketId } }));
    } catch (_) {}
  };

  return (
    <div className="pool-monitor">
      <div className="pool-monitor-header">
        <div className="pool-monitor-title">
          <Ticket size={22} />
          <h1>{t('pool.title', 'Pool-Monitor')}</h1>
          <span className="pool-monitor-count">{poolTickets.length}</span>
        </div>
        {lastUpdated && (
          <div className="pool-monitor-updated">
            <RefreshCw size={14} />
            {t('pool.lastUpdated', 'Stand')} {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>

      {!loaded ? (
        <div className="pool-monitor-empty">{t('pool.loading', 'Lade Pool…')}</div>
      ) : poolTickets.length === 0 ? (
        <div className="pool-monitor-empty">{t('pool.empty', 'Keine offenen Tickets im Pool. 🎉')}</div>
      ) : (
        <div className="pool-monitor-grid">
          {poolTickets.map((tk) => (
            <div
              key={tk.id}
              className={`pool-monitor-card${(tk.hasNewActivity || tk.hasNewEmails) ? ' has-new' : ''}`}
              onClick={() => openTicket(tk.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') openTicket(tk.id); }}
            >
              <div className="pool-monitor-card-top">
                <span className="pool-monitor-number">#{tk.ticketNumber || tk.id}</span>
                <span className="pool-monitor-age">{formatAge(tk.lastActivityAt || tk.createdAt)}</span>
              </div>
              <CalendarTicketTitle ticket={tk} titleClassName="pool-monitor-card-title" />
              <div className="pool-monitor-card-meta">
                <span
                  className="pool-monitor-badge"
                  style={{ backgroundColor: PRIORITY_COLORS[tk.priority] || 'var(--color-secondary)' }}
                >
                  {tk.priority}
                </span>
                <span
                  className="pool-monitor-badge"
                  style={{ backgroundColor: STATUS_COLORS[tk.status] || 'var(--color-secondary)' }}
                >
                  {tk.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PoolMonitor;
